/**
 * General step component for Create Scenario page
 */
import {
    Alert,
    Box,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import React, { memo, useEffect, useState } from 'react';

import { fieldsApi, FieldSummary } from '@/api/fields';
import { FieldCoordinatePicker } from '@/components/Fields/FieldCoordinatePicker';
import { useAuth } from '@/hooks/useAuth';
import { UserRole } from '@/types';
import type { LatLng } from '@/utils/fieldCoordinate';
import { buildPointCoordinate, parsePointCoordinate } from '@/utils/fieldCoordinate';

interface GeneralStepProps {
  scenarioId?: string | null;
  scenarioMode?: 'ml' | 'manual';
  onEnsureScenarioSaved?: () => Promise<string | null>;
  onFieldChange?: (fieldId: string | null) => void;
  onFieldTypeChange?: (fieldType: 'new' | 'existing') => void;
  onNameChange?: (name: string) => void;
  onDescriptionChange?: (description: string) => void;
  onVisibilityChange?: (visibility: 'public' | 'private' | 'company_public') => void;
  onUnitIdChange?: (unitId: number) => void;
  onCoordinateChange?: (coordinate: string | null) => void;
  initialData?: {
    fieldType?: 'new' | 'existing';
    fieldId?: string | null;
    name?: string;
    description?: string;
    visibility?: 'public' | 'private' | 'company_public';
    unitId?: number;
    coordinate?: string | null;
  };
}

export const GeneralStep: React.FC<GeneralStepProps> = memo(({
  scenarioId: _scenarioId,
  scenarioMode: _scenarioMode = 'ml',
  onFieldChange,
  onFieldTypeChange,
  onNameChange,
  onDescriptionChange,
  onVisibilityChange,
  onUnitIdChange,
  onCoordinateChange,
  initialData,
}) => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === UserRole.ADMIN || user?.role === 'admin';
  const [fieldType, setFieldType] = useState<'new' | 'existing'>(initialData?.fieldType || 'new');
  const [selectedFieldId, setSelectedFieldId] = useState<string>(initialData?.fieldId || '');
  const [name, setName] = useState<string>(initialData?.name || '');
  const [description, setDescription] = useState<string>(initialData?.description || '');
  const [visibility, setVisibility] = useState<'public' | 'private' | 'company_public'>(initialData?.visibility || 'private');
  const [unitId, setUnitId] = useState<number>(initialData?.unitId ?? 1);
  const [position, setPosition] = useState<LatLng | null>(
    () => parsePointCoordinate(initialData?.coordinate ?? null),
  );
  const [fields, setFields] = useState<FieldSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    onVisibilityChange?.(visibility);
  }, [visibility, onVisibilityChange]);

  useEffect(() => {
    if (initialData?.unitId != null) {
      setUnitId(initialData.unitId);
    }
  }, [initialData?.unitId]);

  useEffect(() => {
    if (initialData?.coordinate !== undefined) {
      setPosition(parsePointCoordinate(initialData.coordinate));
    }
  }, [initialData?.coordinate]);


  useEffect(() => {
    const loadFields = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fieldsApi.getFieldsList();
        setFields(response.fields);

        if (response.fields.length > 0) {
          setSelectedFieldId(response.fields[0].id);
          onFieldChange?.(response.fields[0].id);
        }
      } catch (err) {
        console.error('Failed to load fields:', err);
        setError('Failed to load fields');
      } finally {
        setLoading(false);
      }
    };

    if (fields.length === 0) {
      loadFields();
    }
  }, []);

  const handleFieldTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFieldType = event.target.value as 'new' | 'existing';
    setFieldType(newFieldType);
    onFieldTypeChange?.(newFieldType);

    if (newFieldType === 'new') {
      setSelectedFieldId('');
      onFieldChange?.(null);
    } else if (fields.length > 0) {
      setSelectedFieldId(fields[0].id);
      onFieldChange?.(fields[0].id);
    }
  };

  const handleFieldChange = (event: any) => {
    const fieldId = event.target.value as string;
    setSelectedFieldId(fieldId);
    onFieldChange?.(fieldId);
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setName(value);
    onNameChange?.(value);
  };

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setDescription(value);
    onDescriptionChange?.(value);
  };

  const handleVisibilityChange = (event: any) => {
    const value = event.target.value as 'public' | 'private' | 'company_public';
    setVisibility(value);
    onVisibilityChange?.(value);
  };

  const handleUnitIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    setUnitId(value);
    onUnitIdChange?.(value);
  };

  const handlePositionChange = (next: LatLng | null) => {
    setPosition(next);
    onCoordinateChange?.(buildPointCoordinate(next?.lat ?? null, next?.lng ?? null));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading fields...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Typography variant="h5" gutterBottom>
        General Settings
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Configure the basic settings for your scenario
      </Typography>

      <FormControl component="fieldset" sx={{ mb: 4, display: 'none' }}>
        <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
          Field Selection
        </FormLabel>
        <RadioGroup
          value={fieldType}
          onChange={handleFieldTypeChange}
          sx={{ gap: 1 }}
        >
          <FormControlLabel
            value="new"
            control={<Radio />}
            label="Calculate for new field"
          />
          {false && (
          <FormControlLabel
            value="existing"
            control={<Radio />}
            label="Calculate for existing field"
          />
          )}
        </RadioGroup>
      </FormControl>

      {false && (
      <FormControl fullWidth sx={{ mb: 4 }}>
        <InputLabel id="field-select-label">Select Field</InputLabel>
        <Select
          labelId="field-select-label"
          value={selectedFieldId}
          label="Select Field"
          onChange={handleFieldChange}
          disabled={fieldType === 'new'}
        >
          {fields.map((field) => (
            <MenuItem key={field.id} value={field.id}>
              <Box>
                <Typography variant="body1" fontWeight={500}>
                  {field.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {field.type.toUpperCase()}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      )}

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Scenario Information
        </Typography>

        <TextField
          fullWidth
          label="Scenario Name"
          placeholder="Enter scenario name"
          value={name}
          onChange={handleNameChange}
          sx={{ mb: 3 }}
        />

        <TextField
          fullWidth
          label="Description"
          placeholder="Enter scenario description"
          multiline
          rows={3}
          value={description}
          onChange={handleDescriptionChange}
          sx={{ mb: 3 }}
        />

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Visibility</InputLabel>
          <Select
            value={visibility}
            onChange={handleVisibilityChange}
            label="Visibility"
          >
            <MenuItem value="private">Private</MenuItem>
            <MenuItem value="company_public">Company Public</MenuItem>
            {isSuperAdmin && (
              <MenuItem value="public">Public</MenuItem>
            )}
          </Select>
        </FormControl>

        <FormControl component="fieldset" sx={{ mb: 3 }}>
          <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
            Production rate units
          </FormLabel>
          <RadioGroup
            row
            value={String(unitId)}
            onChange={handleUnitIdChange}
          >
            <FormControlLabel value="1" control={<Radio />} label="Tons" />
            <FormControlLabel value="2" control={<Radio />} label="Barrels" />
          </RadioGroup>
        </FormControl>

        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Location (optional)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Set a map location for this scenario to help pick nearby analogues. Leave empty if unknown.
        </Typography>
        <FieldCoordinatePicker
          enabled
          position={position}
          onChange={handlePositionChange}
          mapHeight={280}
          markerKind="scenario"
          pinLabel={name.trim() || 'Scenario'}
          fields={fields}
        />
      </Box>
    </Box>
  );
});
